import { ProgressBarModule } from 'primeng/progressbar';
import { DatePipe, NgClass } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  InjectionToken,
  input,
  output,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Column, FormField } from '@fsf/types';
import { LARAVEL_ENDPOINT, LaravelApi, LaravelResult } from '@fsf/util';
import { _, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { provideComponentDefaultTranslations } from '../../util/defaults-translation-handler';
import translations from './i18n';
import { query } from '@vortechron/query-builder-ts';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ChipModule } from 'primeng/chip';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { Table, TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { finalize, Observable, of } from 'rxjs';
import * as z from 'zod';
import { ResolveLabelPipe } from './resolve-label.pipe';

export type BeforeSaveFn = (
  formValue: Record<string, unknown>,
  original: Record<string, unknown> | null,
) => Observable<boolean>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type UserTableSchema = z.ZodObject<any>;

/** Server-side boolean filter, sent as filter[key]=1. label is a translation key. */
export interface UserTableQuickFilter {
  key: string;
  label: string;
}

export const USER_API = new InjectionToken<LaravelApi<Record<string, unknown>>>(
  'laravel.api.users',
);

export const USER_SCHEMA = new InjectionToken<UserTableSchema>(
  'user.table.schema',
);

_([
  'label.addUser',
  'label.cancel',
  'label.clearFilter',
  'label.delete',
  'label.errorDeleting',
  'label.errorHeader',
  'label.invalidData',
  'label.manageUserFields',
  'label.onboardingMailError',
  'label.onboardingMailSent',
  'label.save',
  'label.sendOnboardingMail',
  'label.successfullyDeleted',
  'label.successfullyDeletedHeader',
  'label.successHeader',
  'label.search',
  'user.create',
  'user.created',
  'user.createFailed',
  'user.deleteConfirmMessage',
  'user.deleteHeader',
  'user.edit',
  'user.updated',
  'user.updateFailed',
]);

@Component({
  selector: 'ui-user-table',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    TagModule,
    MultiSelectModule,
    SelectModule,
    TranslatePipe,
    NgClass,
    ToggleButtonModule,
    ToggleSwitchModule,
    ChipModule,
    DatePickerModule,
    ProgressBarModule,
    DatePipe,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    ButtonModule,
    DialogModule,
    ConfirmDialogModule,
    ToastModule,
    TooltipModule,
    ResolveLabelPipe,
  ],
  templateUrl: './user-table.component.html',
  styleUrl: './user-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    MessageService,
    ConfirmationService,
    provideComponentDefaultTranslations(translations),
  ],
})
export class UserTableComponent {
  #api = inject(USER_API);
  #schema = inject(USER_SCHEMA);
  #router = inject(Router);
  #fb = inject(FormBuilder);
  #messageService = inject(MessageService);
  #confirmationService = inject(ConfirmationService);
  #translateService = inject(TranslateService);
  #http = inject(HttpClient);
  #endpoint = inject(LARAVEL_ENDPOINT);

  isLoading = signal<boolean>(false);
  totalRecords = signal<number>(0);
  users = signal<Record<string, unknown>[]>([]);
  tagOptions = signal<Record<string, string[]>>({});
  selectOptions = signal<Record<string, { label: string; value: number }[]>>(
    {},
  );
  groupedSelectOptions = signal<
    Record<
      string,
      { label: string; items: { label: string; value: number }[] }[]
    >
  >({});

  activeFilters = signal<{ field: string; label: string; value: string }[]>([]);

  showManageUserFields = input(false);
  quickFilters = input<UserTableQuickFilter[]>([]);
  activeQuickFilters = signal<string[]>([]);
  beforeSave = input<BeforeSaveFn | null>(null);
  manageUserFields = output<Record<string, unknown>>();

  currentUser: Record<string, unknown> | null = null;
  userPopupVisible = false;

  cols: Column[] = this.#schema.meta()?.columns ?? [];
  filterFields: string[] = this.#schema.meta()?.globalFilterFields ?? [];
  formFields: FormField[] = this.#schema.meta()?.formFields ?? [];
  userForm: FormGroup = this.#buildForm();

  constructor() {
    this.#loadTagOptions();
  }

  #lastLazyEvent: TableLazyLoadEvent | null = null;

  #collectedValues = signal<Record<string, string[]>>({});

  filterOptions = computed(() => {
    const collected = this.#collectedValues();
    const tags = this.tagOptions();
    const options: Record<string, { label: string; value: string }[]> = {};

    for (const col of this.cols) {
      if (col.matchMode === 'in') {
        const field = col.field as string;
        if (col.textOptions?.length) {
          options[field] = col.textOptions.map((v) => ({
            label: v,
            value: v,
          }));
        } else if (tags[field]?.length) {
          options[field] = tags[field].map((v) => ({
            label: v,
            value: v,
          }));
        } else {
          const values = collected[field] ?? [];
          options[field] = values.map((v) => ({
            label: v,
            value: v,
          }));
        }
      }
    }

    return options;
  });

  #buildForm(): FormGroup {
    const controls: Record<string, unknown> = {};
    for (const field of this.formFields) {
      controls[field.field as string] = [
        null,
        field.required ? Validators.required : null,
      ];
    }
    return this.#fb.group(controls);
  }

  loadData(e: TableLazyLoadEvent) {
    this.#lastLazyEvent = e;
    this.#updateActiveFilters(e);
    const q = query('');

    // Pagination
    if (typeof e.rows === 'number' && typeof e.first === 'number') {
      q.param('perPage', `${e.rows}`).page(e.first / e.rows + 1);
    }

    // Filter
    Object.entries(e.filters || {}).map(([field, filters]) => {
      if (filters) {
        const filterValues = (
          filters instanceof Array ? filters : [filters]
        ).reduce(
          (acc, x) => (x.value === null ? acc : [...acc, x.value]),
          [] as string[],
        );
        if (filterValues.length) {
          q.filter(field, filterValues.join(','));
        }
      }
    });

    for (const key of this.activeQuickFilters()) {
      q.filter(key, '1');
    }

    // Sort
    if (e.multiSortMeta instanceof Array) {
      q.sort(
        ...e.multiSortMeta.map(({ field, order }) =>
          order < 1 ? `-${field}` : field,
        ),
      );
    }

    this.isLoading.set(true);
    this.#api
      .readMany(q.build())
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (result) => {
          const parsed: Record<string, unknown>[] = [];
          for (const user of result.data) {
            const parseResult = this.#schema.safeParse(user);
            if (parseResult.success) {
              parsed.push(parseResult.data as Record<string, unknown>);
            } else {
              console.error(parseResult.error);
            }
          }
          this.users.set(parsed);
          this.#collectFilterValues(parsed);
          this.totalRecords.set(result.meta.pagination.total);
        },
        error: (e: HttpErrorResponse) => {
          // TODO: implement signout in @freesixtyfive/auth
          if (e.status === 401) {
            localStorage.clear();
            this.#router.navigateByUrl('/');
          }
        },
      });
  }

  reloadData() {
    if (this.#lastLazyEvent) {
      this.loadData(this.#lastLazyEvent);
    }
  }

  onManageUserFields(user: Record<string, unknown>) {
    this.manageUserFields.emit(user);
  }

  onRowEditInit(user: Record<string, unknown>) {
    const patchData: Record<string, unknown> = {};
    for (const field of this.formFields) {
      let value = user[field.field as string];
      if (field.type === 'date' && typeof value === 'string') {
        value = new Date(value);
      }
      patchData[field.field as string] = value;
    }
    this.userForm.patchValue(patchData);
    this.currentUser = user;
    this.userPopupVisible = true;
  }

  onRowDeleteInit(event: Event, user: Record<string, unknown>) {
    this.#confirmationService.confirm({
      target: event.target as EventTarget,
      message: this.#translateService.instant('user.deleteConfirmMessage'),
      header: this.#translateService.instant('user.deleteHeader'),
      rejectButtonProps: {
        label: this.#translateService.instant('label.cancel'),
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: this.#translateService.instant('label.delete'),
        severity: 'danger',
      },
      accept: () => {
        this.#api.removeOne(user['id'] as string).subscribe({
          next: () => {
            this.reloadData();
            this.#messageService.add({
              severity: 'success',
              summary: this.#translateService.instant(
                'label.successfullyDeletedHeader',
              ),
              detail: this.#translateService.instant(
                'label.successfullyDeleted',
              ),
            });
          },
          error: () => {
            this.#messageService.add({
              severity: 'error',
              summary: this.#translateService.instant('label.errorHeader'),
              detail: this.#translateService.instant('label.errorDeleting'),
            });
          },
        });
      },
    });
  }

  showUserPopup() {
    const defaults = this.#schema.meta()?.emptyRecord?.();
    this.userForm.reset(defaults);
    this.currentUser = null;
    this.userPopupVisible = true;
  }

  onSubmit() {
    if (!this.userForm.valid) {
      this.userForm.markAllAsTouched();
      // PrimeNG highlights invalid inputs only when dirty
      Object.values(this.userForm.controls).forEach((c) => c.markAsDirty());
      return;
    }

    const formValue = { ...this.userForm.value };
    for (const key of Object.keys(formValue)) {
      if (formValue[key] instanceof Date) {
        const d = formValue[key] as Date;
        formValue[key] =
          `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      }
    }
    const record = { ...formValue, id: this.currentUser?.['id'] ?? '' };
    const result = this.#schema.safeEncode(record);

    if (!result.success) {
      console.error('Validation error:', result.error);
      this.#messageService.add({
        severity: 'error',
        summary: this.#translateService.instant('label.errorHeader'),
        detail: this.#translateService.instant('label.invalidData'),
      });
      return;
    }

    const { id, ...data } = result.data;
    const beforeSaveFn = this.beforeSave();

    if (beforeSaveFn) {
      beforeSaveFn(formValue, this.currentUser).subscribe((proceed) => {
        if (proceed) {
          this.#performSave(data);
        }
      });
    } else {
      this.#performSave(data);
    }
  }

  #performSave(data: Record<string, unknown>) {
    if (this.currentUser?.['id']) {
      this.#api.updateOne(this.currentUser['id'] as string, data).subscribe({
        next: () => {
          this.closeUserPopup();
          this.reloadData();
          this.#messageService.add({
            severity: 'success',
            summary: this.#translateService.instant('label.successHeader'),
            detail: this.#translateService.instant('user.updated'),
          });
        },
        error: () => {
          this.#messageService.add({
            severity: 'error',
            summary: this.#translateService.instant('label.errorHeader'),
            detail: this.#translateService.instant('user.updateFailed'),
          });
        },
      });
    } else {
      this.#api.addOne(data).subscribe({
        next: () => {
          this.closeUserPopup();
          this.reloadData();
          this.#messageService.add({
            severity: 'success',
            summary: this.#translateService.instant('label.successHeader'),
            detail: this.#translateService.instant('user.created'),
          });
        },
        error: () => {
          this.#messageService.add({
            severity: 'error',
            summary: this.#translateService.instant('label.errorHeader'),
            detail: this.#translateService.instant('user.createFailed'),
          });
        },
      });
    }
  }

  onSendOnboardingMail(user: Record<string, unknown>) {
    const email = user['email'] as string;
    if (!email) return;

    this.#http
      .post(`${this.#endpoint}/sendOnboardingMailToUser`, { email })
      .subscribe({
        next: () => {
          this.#messageService.add({
            severity: 'success',
            summary: this.#translateService.instant('label.successHeader'),
            detail: this.#translateService.instant('label.onboardingMailSent'),
          });
        },
        error: () => {
          this.#messageService.add({
            severity: 'error',
            summary: this.#translateService.instant('label.errorHeader'),
            detail: this.#translateService.instant('label.onboardingMailError'),
          });
        },
      });
  }

  toggleQuickFilter(table: Table, key: string) {
    this.activeQuickFilters.update((active) =>
      active.includes(key) ? active.filter((k) => k !== key) : [...active, key],
    );
    table.first = 0;
    if (this.#lastLazyEvent) {
      this.loadData({ ...this.#lastLazyEvent, first: 0 });
    }
  }

  clear(table: Table) {
    this.activeQuickFilters.set([]);
    table.clear();
    this.activeFilters.set([]);
  }

  removeFilter(table: Table, field: string) {
    const filterMeta = table.filters[field];
    if (filterMeta) {
      if (Array.isArray(filterMeta)) {
        filterMeta.forEach((f) => (f.value = null));
      } else {
        filterMeta.value = null;
      }
    }
    table._filter();
  }

  #updateActiveFilters(e: TableLazyLoadEvent) {
    const colMap = new Map(this.cols.map((c) => [c.filterField as string, c]));

    const chips = Object.entries(e.filters || {}).flatMap(([field, filters]) => {
      if (!filters) return [];
      const filterArr = Array.isArray(filters) ? filters : [filters];
      return filterArr.flatMap((f: any) => {
        if (f.value == null || f.value === '') return [];
        const col = colMap.get(field);
        const label = col
          ? this.#translateService.instant(col.header as string)
          : field;
        const displayValue = Array.isArray(f.value)
          ? f.value.join(', ')
          : String(f.value);
        return [{ field, label, value: displayValue }];
      });
    });
    this.activeFilters.set(chips);
  }

  onGlobalFilter(table: Table, event: Event) {
    table.filterGlobal((event.target as HTMLInputElement).value, 'contains');
  }

  closeUserPopup() {
    this.userPopupVisible = false;
    this.userForm.reset();
    this.currentUser = null;
  }

  #collectFilterValues(data: Record<string, unknown>[]) {
    this.#collectedValues.update((current) => {
      const updated = { ...current };
      for (const col of this.cols) {
        if (col.matchMode === 'in' && !col.textOptions?.length) {
          const field = col.field as string;
          const existing = new Set(updated[field] ?? []);
          for (const item of data) {
            const val = item[field];
            if (val) existing.add(String(val));
          }
          updated[field] = [...existing].sort();
        }
      }
      return updated;
    });
  }

  #loadTagOptions() {
    const tagEndpoints = new Set<{ field: string; endpoint: string }>();
    const selectEndpoints = new Set<{
      field: string;
      endpoint: string;
      valueField: string;
      labelField: string;
      groupField?: string;
    }>();

    for (const col of this.cols) {
      if (col.optionsEndpoint && col.optionValueField) {
        selectEndpoints.add({
          field: col.field as string,
          endpoint: col.optionsEndpoint,
          valueField: col.optionValueField,
          labelField: col.optionLabelField ?? 'name',
          groupField: col.optionGroupField,
        });
      } else if (col.optionsEndpoint) {
        tagEndpoints.add({
          field: col.field as string,
          endpoint: col.optionsEndpoint,
        });
      }
    }
    for (const field of this.formFields) {
      if (field.optionsEndpoint && field.optionValueField) {
        selectEndpoints.add({
          field: field.field as string,
          endpoint: field.optionsEndpoint,
          valueField: field.optionValueField,
          labelField: field.optionLabelField ?? 'name',
          groupField: field.optionGroupField,
        });
      } else if (field.optionsEndpoint) {
        tagEndpoints.add({
          field: field.field as string,
          endpoint: field.optionsEndpoint,
        });
      }
    }

    const apiBase = this.#endpoint.substring(
      0,
      this.#endpoint.indexOf('/api/') + 4,
    );

    for (const { field, endpoint } of tagEndpoints) {
      this.#http
        .get<LaravelResult<Record<string, unknown>[]>>(`${apiBase}/${endpoint}`)
        .subscribe({
          next: (result) => {
            const names = result.data.map((item) =>
              typeof item === 'string' ? item : String(item['name']),
            );
            this.tagOptions.update((opts) => ({
              ...opts,
              [field]: names,
            }));
          },
        });
    }

    for (const { field, endpoint, valueField, labelField, groupField } of selectEndpoints) {
      this.#http
        .get<LaravelResult<Record<string, unknown>[]>>(`${apiBase}/${endpoint}`)
        .subscribe({
          next: (result) => {
            const options = result.data.map((item) => ({
              label: String(item[labelField] ?? ''),
              value: Number(item[valueField]),
            }));
            this.selectOptions.update((opts) => ({
              ...opts,
              [field]: options,
            }));

            if (groupField) {
              const groups = new Map<string, { label: string; value: number }[]>();
              for (const item of result.data) {
                const key = String(item[groupField] ?? '—');
                if (!groups.has(key)) groups.set(key, []);
                groups.get(key)!.push({
                  label: String(item[labelField] ?? ''),
                  value: Number(item[valueField]),
                });
              }
              const grouped = Array.from(groups.entries())
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([label, items]) => ({ label, items }));
              this.groupedSelectOptions.update((opts) => ({
                ...opts,
                [field]: grouped,
              }));
            }
          },
        });
    }
  }
}
